import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Brain, Loader2, Send, CheckCircle2 } from 'lucide-react';
import RichChatMessage from '@/components/chat/RichChatMessage';

function reasonScoreClasses(score) {
  if (score == null) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (score >= 4) return 'bg-emerald-50 text-emerald-700 border-emerald-300';
  if (score >= 2) return 'bg-amber-50 text-amber-700 border-amber-300';
  return 'bg-rose-50 text-rose-700 border-rose-300';
}

export default function SchoolTeacherAttemptReview() {
  const navigate = useNavigate();
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [me, setMe] = useState(null);
  const school = useSchool(me?.school_id);

  const [overall, setOverall] = useState('');
  const [perQ, setPerQ] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/teacher/me/attempts/${tid}`);
      setData(res.data);
      const at = res.data?.attempt;
      setOverall(at?.teacher_overall_feedback || '');
      const pq = {};
      for (const item of at?.teacher_feedback_per_question || []) {
        pq[item.question_id] = item.comment;
      }
      setPerQ(pq);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load attempt');
    } finally { setLoading(false); }
  }, [tid]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
      } catch {/* */}
    })();
    load();
  }, [load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;
  const a = data?.assessment;
  const at = data?.attempt;
  const questions = data?.questions || [];
  const student = data?.student || {};
  const isHoldback = a?.release_mode === 'teacher_approved';
  const isReleased = at?.status === 'released';

  const answersByQid = useMemo(() => {
    const map = {};
    for (const ans of at?.answers || []) map[ans.question_id] = ans;
    return map;
  }, [at]);

  const saveFeedback = async () => {
    setSavingFeedback(true);
    try {
      const per_question = Object.entries(perQ)
        .filter(([, v]) => (v || '').trim().length > 0)
        .map(([qid, comment]) => ({ question_id: qid, comment }));
      await api.post(`/teacher/me/attempts/${tid}/feedback`, {
        overall_feedback: overall,
        per_question,
      });
      toast.success('Feedback saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingFeedback(false); }
  };

  const release = async () => {
    if (!overall.trim()) {
      toast.error('Please write overall feedback before releasing.');
      return;
    }
    setReleasing(true);
    try {
      await saveFeedbackInternal(); // ensure latest is saved
      await api.post(`/teacher/me/attempts/${tid}/release`);
      toast.success('Released — student and parent will be notified.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release failed');
    } finally { setReleasing(false); }
  };

  const saveFeedbackInternal = async () => {
    const per_question = Object.entries(perQ)
      .filter(([, v]) => (v || '').trim().length > 0)
      .map(([qid, comment]) => ({ question_id: qid, comment }));
    await api.post(`/teacher/me/attempts/${tid}/feedback`, {
      overall_feedback: overall,
      per_question,
    });
  };

  const overrideReasonScore = async (questionId, value) => {
    try {
      const body = {
        question_id: questionId,
        reason_score: value === 'null' ? null : Number(value),
      };
      await api.post(`/teacher/me/attempts/${tid}/reason-override`, body);
      toast.success(value === 'null' ? 'Override cleared' : `Score set to ${value}/5`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update score');
    }
  };

  if (loading || !at) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-32"
      data-testid="school-teacher-attempt-review"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900" data-testid="attempt-title">
              {a?.title}
            </h1>
            <p className="text-xs text-slate-500">
              {student.student_name || student.name} · Score {at.total_score}/{at.max_score} ({at.percent}%)
              {at.reason_score_avg != null && (
                <span className="ml-2" data-testid="teacher-reason-avg">
                  · Reasoning {at.reason_score_avg.toFixed(1)}/5
                </span>
              )}
            </p>
          </div>
          <Badge variant="outline" className={isReleased ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-amber-50 text-amber-700 border-amber-300'}>
            {isReleased ? 'Released' : 'Awaiting review'}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-4">
        {questions.map((q, idx) => {
          const ans = answersByQid[q.question_id];
          const correct = q.correct_option_id;
          const chosen = ans?.chosen_option_id;
          const isCorrect = chosen === correct;
          return (
            <div
              key={q.question_id}
              className={`bg-white border rounded-xl p-5 shadow-sm ${isCorrect ? 'border-emerald-200' : 'border-rose-200'}`}
              data-testid={`attempt-q-${q.question_id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-bold text-slate-700">Q{idx + 1}</span>
                <Badge variant="outline" className={isCorrect ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-rose-50 text-rose-700 border-rose-300'}>
                  {isCorrect ? 'Correct' : 'Incorrect'}
                </Badge>
              </div>
              <div className="text-slate-900 font-medium mb-3 overflow-x-auto">
                <RichChatMessage text={q.prompt} />
              </div>

              <div className="space-y-2 mb-3">
                {q.options.map((opt) => {
                  const isCorrectOpt = opt.id === correct;
                  const isChosen = opt.id === chosen;
                  let cls = 'bg-slate-50 border border-slate-100';
                  if (isCorrectOpt) cls = 'bg-emerald-50 border border-emerald-200';
                  else if (isChosen) cls = 'bg-rose-50 border border-rose-200';
                  return (
                    <div key={opt.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${cls}`}>
                      <span className="font-mono text-slate-500">{opt.id}.</span>
                      <div className="flex-1 overflow-x-auto">
                        <RichChatMessage text={opt.text} />
                      </div>
                      {isCorrectOpt && <Badge variant="outline" className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Correct</Badge>}
                      {!isCorrectOpt && isChosen && <Badge variant="outline" className="ml-auto bg-rose-100 text-rose-700 border-rose-300 text-[10px]">Chose this</Badge>}
                    </div>
                  );
                })}
              </div>

              <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 text-sm text-slate-700 mb-3">
                <strong className="text-indigo-700">Why it's correct:</strong>
                <div className="mt-1 overflow-x-auto">
                  <RichChatMessage text={q.correct_reasoning} />
                </div>
              </div>

              <div className="bg-amber-50/70 border border-amber-100 rounded-lg p-3 text-sm text-slate-700 mb-3">
                <strong className="text-amber-700">Student's reason:</strong>
                {ans?.student_reason ? (
                  <div className="mt-1 overflow-x-auto">
                    <RichChatMessage text={ans.student_reason} />
                  </div>
                ) : (
                  <span className="italic text-slate-400 ml-1">No reason captured</span>
                )}
              </div>

              {/* Phase 5-B: LLM rubric score + teacher override */}
              <div
                className={`rounded-lg p-3 text-sm border mb-3 ${reasonScoreClasses(ans?.reason_score)}`}
                data-testid={`reason-row-${q.question_id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Brain size={14} />
                  <span className="font-semibold">Reasoning quality</span>
                  {ans?.reason_score_override_by && (
                    <Badge variant="outline" className="text-[10px] ml-1 bg-white">Teacher override</Badge>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <Select
                      value={ans?.reason_score == null ? 'null' : String(ans.reason_score)}
                      onValueChange={(v) => overrideReasonScore(q.question_id, v)}
                      disabled={isReleased || !ans}
                    >
                      <SelectTrigger
                        className="h-7 w-20 bg-white"
                        data-testid={`reason-override-${q.question_id}`}
                      >
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— (clear)</SelectItem>
                        {[0, 1, 2, 3, 4, 5].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n} / 5</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {ans?.reason_rationale ? (
                  <p className="text-xs leading-relaxed text-slate-700">{ans.reason_rationale}</p>
                ) : (
                  <p className="text-xs italic text-slate-500 flex items-center gap-1.5">
                    <Loader2 size={11} className="animate-spin" /> Grading in progress…
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Your comment for this question (optional)</Label>
                <Textarea
                  value={perQ[q.question_id] || ''}
                  onChange={(e) => setPerQ({ ...perQ, [q.question_id]: e.target.value })}
                  rows={2}
                  placeholder="Optional feedback for this question"
                  disabled={isReleased}
                  data-testid={`per-q-feedback-${q.question_id}`}
                />
              </div>
            </div>
          );
        })}

        <div className="bg-white border rounded-xl p-5">
          <Label className="text-sm font-semibold text-slate-700">
            Overall feedback {isHoldback && !isReleased && <span className="text-rose-500">*</span>}
          </Label>
          <Textarea
            value={overall}
            onChange={(e) => setOverall(e.target.value)}
            rows={4}
            placeholder="Write a short, encouraging note about the student's performance and reasoning."
            disabled={isReleased}
            data-testid="overall-feedback"
            className="mt-2"
          />
          {isHoldback && !isReleased && (
            <p className="text-xs text-slate-500 mt-2">Required to release results to the student & parent.</p>
          )}
        </div>
      </main>

      {!isReleased && (
        <div className="fixed bottom-0 inset-x-0 border-t bg-white/95 backdrop-blur" data-testid="release-bar">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={saveFeedback} disabled={savingFeedback || releasing} data-testid="save-feedback-btn">
              {savingFeedback ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
              Save feedback
            </Button>
            {isHoldback && (
              <Button
                onClick={release}
                disabled={releasing || !overall.trim()}
                style={{ backgroundColor: brandColor }}
                className="text-white"
                data-testid="release-btn"
              >
                {releasing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                Release results
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
